import React from "react";
import { Link } from "wouter";

import Button, { ButtonTypes } from "../../../components/Button";
import { Skeleton } from "../../../components/Skeleton";
import { EmptyState } from "../../../components/States";
import { ObservedAt } from "../components/ObservedAt";
import { RefreshFailedNotice } from "../components/RefreshFailedNotice";
import { SectionUnavailable } from "../components/SectionUnavailable";
import { useDetailNavigation } from "../hooks/useDetailNavigation";
import { WORK_QUEUE_PATH } from "../paths";
import {
  ActionLink,
  BackLink,
  Caption,
  Card,
  PageContent,
  PageHeader,
  PageHeading,
  PageTitle,
  QueueLayout,
  StatusNote,
} from "../styles";
import { plural } from "../utils/format";
import { JobDetail } from "./components/JobDetail";
import { JobList } from "./components/JobList";
import { JobsToolbar } from "./components/JobsToolbar";
import { useJobRecovery } from "./hooks/useJobRecovery";
import { useJobs } from "./hooks/useJobs";
import { keyOfJob } from "./utils/describeJob";

export const JobsPage: React.FC = () => {
  const {
    section,
    query,
    state,
    age,
    jobs,
    total,
    selected,
    isFiltered,
    changeQuery,
    changeState,
    changeAge,
    clearFilters,
    select,
    isRefreshing,
    refresh,
  } = useJobs();
  const { isStacked, listRef, detailRef, openDetail, backToList } =
    useDetailNavigation();
  const { isRecovering, noteOf, departedNotesIn, recover } = useJobRecovery();

  const isAvailable = section.state === "available";
  const departedNotes = isAvailable ? departedNotesIn(jobs) : [];
  const isEmpty = isAvailable && selected === null;
  const refreshLabel = isRefreshing ? "Refreshing…" : "Refresh";

  const handleSelect = (key: string) => {
    select(key);
    openDetail();
  };

  const isCapped = jobs.length < total;
  const cappedLabel = `Showing ${jobs.length} of ${total}, narrow the filters`;
  const fullLabel = plural(total, "job");
  const countLabel = isCapped ? cappedLabel : fullLabel;

  return (
    <PageContent>
      <BackLink>
        <Link href={WORK_QUEUE_PATH} asChild>
          <ActionLink>Back to work queue</ActionLink>
        </Link>
      </BackLink>
      <PageHeader>
        <PageTitle>
          <PageHeading>Background jobs</PageHeading>
          <Caption>Snapshot and price backfill jobs, problems first.</Caption>
          {isAvailable && <ObservedAt observedAt={section.observedAt} />}
        </PageTitle>
        <Button
          type={ButtonTypes.cancel}
          size="small"
          disabled={isRefreshing}
          onClick={refresh}
        >
          {refreshLabel}
        </Button>
      </PageHeader>
      <JobsToolbar
        query={query}
        state={state}
        age={age}
        countLabel={isAvailable ? countLabel : null}
        onQueryChange={changeQuery}
        onStateChange={changeState}
        onAgeChange={changeAge}
      />
      {section.state === "loading" && (
        <Skeleton variant="rectangular" height="12rem" />
      )}
      {section.state === "unavailable" && (
        <Card aria-label="Background jobs">
          <SectionUnavailable
            observedAt={section.observedAt}
            onRetry={refresh}
          />
        </Card>
      )}
      {isAvailable && section.isRefreshFailed && (
        <RefreshFailedNotice sectionName="Background jobs" onRetry={refresh} />
      )}
      {departedNotes.map(({ key, tone, message }) => (
        <StatusNote key={key} role="status" $tone={tone}>
          {message}
        </StatusNote>
      ))}
      {isEmpty && isFiltered && (
        <EmptyState
          title="No jobs match"
          description="Change the search or filters to see other jobs."
          action={
            <Button
              type={ButtonTypes.cancel}
              size="small"
              onClick={clearFilters}
            >
              Clear filters
            </Button>
          }
        />
      )}
      {isEmpty && !isFiltered && (
        <EmptyState
          title="No background jobs recorded"
          description="Snapshot and price backfill jobs will appear here once they are created."
        />
      )}
      {selected && (
        <QueueLayout>
          <JobList
            jobs={jobs}
            selectedKey={keyOfJob(selected)}
            listRef={listRef}
            onSelect={handleSelect}
          />
          <JobDetail
            job={selected}
            detailRef={detailRef}
            isStacked={isStacked}
            onBackToList={backToList}
            recoveryNote={noteOf(selected)}
            isRecovering={isRecovering}
            onRecover={recover}
          />
        </QueueLayout>
      )}
    </PageContent>
  );
};
